import type { Ref } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import type { PropsOf } from '@emotion/react';
import { css } from '@emotion/react';
import * as tokens from '@tokens';
import { CloseAction } from '@components/Actions/CloseAction/CloseAction';
import type { DismissibleProps } from '@common/Dismissible';
import { useStrings } from '@hooks/useStrings';
import { useInterpolate } from '@hooks/useInterpolate';

export type TagProps = Omit<
  PropsOf<'div'>,
  'children' | 'className' | 'size'
> & {
  /**
   * Text shown in the tag. Should be a few words. Will be truncated depending
   * on the container.
   */
  children: string;
  /**
   * Styles override.
   */
  className?: string;
  /**
   * Size of the Tag. Default as `m`
   */
  size?: 's' | 'm';
  /**
   * Prefix content that appears before the tag content
   */
  prefixContent?: JSX.Element;
  /** Focus Ref */
  focusRef?: Ref<HTMLButtonElement>;
} & DismissibleProps<boolean>;

const MIN_SHRINKING_SIZE = 100;

export const Label = styled.span`
  ${tokens.truncateText()}
`;

export const PrefixWrapper = styled.div`
  padding-inline-end: ${tokens.spacer.x1};
  display: flex;
  align-items: center;
`;

export const Container = styled('div', {
  shouldForwardProp: (prop: string) =>
    prop !== 'isDismissible' &&
    prop !== 'shouldShrink' &&
    prop !== 'size' &&
    prop !== 'hasOverflow',
})<{
  isDismissible: boolean;
  shouldShrink: boolean;
  hasOverflow: boolean;
  size: 's' | 'm';
}>`
  display: inline-flex;
  align-items: center;
  color: ${tokens.color.text_default};
  background-color: ${tokens.color.cobalt_100};
  padding: 0;
  padding-left: ${tokens.spacer.x2};
  padding-right: ${({ isDismissible }) =>
    isDismissible ? tokens.spacer.x1 : tokens.spacer.x2};
  max-width: 100%;
  flex: 0 ${({ shouldShrink }) => (shouldShrink ? 1 : 0)} auto;
  overflow: hidden;
  min-width: ${({ hasOverflow }) => (hasOverflow ? MIN_SHRINKING_SIZE : 0)}px;
  ${tokens.text('body2')};
  height: ${tokens.spacer.x4};
  border-radius: 24px;
  ${({ size }) =>
    size === 's' &&
    `
    ${tokens.text('caption')};
    height: ${tokens.spacer.x3};
  `};
`;

/**
 * Small labels that help qualify information.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/02361d-tag/b/803f8a Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-tag--basic-tag Code documentation}
 *
 * @example
 * <Tag>My tag</Tag>
 */
export const Tag = ({
  children,
  isDismissible = false,
  onDismiss,
  size = 'm',
  focusRef,
  prefixContent,
  ...props
}: TagProps): JSX.Element => {
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canFlexShrink, setCanFlexShrink] = useState(false);
  const labelRef = useRef<HTMLSpanElement>(null);

  const { tag } = useStrings();
  const interpolate = useInterpolate();

  useLayoutEffect(() => {
    const { offsetWidth, scrollWidth } = labelRef.current;
    setHasOverflow(offsetWidth < scrollWidth);
    setCanFlexShrink(scrollWidth > MIN_SHRINKING_SIZE);
  }, [labelRef, children, canFlexShrink]);

  return (
    <Container
      {...props}
      isDismissible={isDismissible}
      shouldShrink={canFlexShrink}
      hasOverflow={hasOverflow}
      title={hasOverflow ? children : undefined}
      size={size}
    >
      {prefixContent && <PrefixWrapper>{prefixContent}</PrefixWrapper>}
      <Label ref={labelRef}>{children}</Label>
      {isDismissible && (
        <CloseAction
          ref={focusRef}
          onClick={onDismiss}
          description={interpolate(tag.closeActionDescription, {
            tagName: children,
          })}
          css={css`
            margin-left: ${tokens.spacer.x1};
          `}
          iconSize={size}
        />
      )}
    </Container>
  );
};
